import { useState, useEffect } from "react";
import { PageHeader } from "@/components/PageHeader";
import { MatchCard, Match } from "@/components/MatchCard";
import { MatchListItem } from "@/components/MatchListItem";
import { Input } from "@/components/ui/input";
import { Search, Filter } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { ViewToggle } from "@/components/common/ViewToggle";
import { usePersistedViewMode } from "@/utils/viewMode";
import { MatchesStats } from "@/components/matches/MatchesStats";

const Matches = () => {
	const [matches, setMatches] = useState<Match[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const [viewMode, setViewMode] = usePersistedViewMode('matches', 'card');

	useEffect(() => {
		fetchMatches();
	}, []);

	async function fetchMatches() {
		try {
			setIsLoading(true);
			
			const { data: matchesData, error: matchesError } = await supabase
				.from('matches')
				.select(`
					id,
					score,
					description,
					strengths,
					status,
					created_at,
					candidate_id,
					job_id,
					candidates (
						id,
						name
					),
					jobs (
						id,
						title,
						company_id,
						companies (
							id,
							name
						)
					)
				`);
			
			if (matchesError) throw matchesError;

			if (matchesData) {
				const formattedMatches: Match[] = matchesData.map(match => ({
					id: match.id,
					candidateName: match.candidates?.name || "Unknown Candidate",
					candidateId: match.candidate_id || "",
					jobId: match.job_id || "",
					jobTitle: match.jobs?.title || "Unknown Position",
					companyId: match.jobs?.companies?.id || "",
					companyName: match.jobs?.companies?.name || "Unknown Company",
					score: match.score,
					description: match.description || "",
					strengths: match.strengths || [],
					status: match.status,
					createdAt: match.created_at
				}));
				
				setMatches(formattedMatches);
			}
		} catch (error) {
			console.error("Error fetching matches:", error);
			toast({
				title: "Failed to load matches",
				description: "An error occurred while loading matches.",
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	}

	const filteredMatches = matches.filter(match => 
		match.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
		match.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
		match.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
		match.description.toLowerCase().includes(searchTerm.toLowerCase())
	);

	return (
		<div>
			<PageHeader 
				title="Matches"
				description="Here are your champions!"
			/>
			
			<MatchesStats matches={filteredMatches} isLoading={isLoading} />
			
			<div className="mb-6 flex flex-col sm:flex-row gap-3">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
					<Input 
						type="text"
						placeholder="Search matches..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="pl-10"
					/>
				</div>
				
				<div className="flex gap-2">
					<Button variant="outline" className="flex items-center gap-2">
						<Filter className="h-4 w-4" />
						<span>Score</span>
					</Button>
					<ViewToggle
						viewMode={viewMode}
						onViewModeChange={setViewMode}
						label=""
					/>
				</div>
			</div>
			
			{isLoading ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{[1, 2, 3, 4, 5, 6].map(i => (
						<div key={i} className="bg-white rounded-lg shadow p-5 animate-pulse">
							<div className="flex justify-between items-center mb-3">
								<div className="h-5 bg-gray-200 rounded w-1/3"></div>
								<div className="h-8 w-8 bg-gray-200 rounded-full"></div>
							</div>
							<div className="h-4 bg-gray-200 rounded mb-4"></div>
							<div className="space-y-2">
								<div className="h-3 bg-gray-200 rounded"></div>
								<div className="h-3 bg-gray-200 rounded"></div>
								<div className="h-3 bg-gray-200 rounded"></div>
							</div>
						</div>
					))}
				</div>
			) : (
				<div className={viewMode === 'card' 
					? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' 
					: 'flex flex-col gap-4'
				}>
					{filteredMatches.map(match => (
						viewMode === 'card' 
							? <MatchCard key={match.id} match={match} />
							: <MatchListItem key={match.id} match={match} />
					))}
				</div>
			)}
			
			{!isLoading && filteredMatches.length === 0 && (
				<div className="text-center p-8 bg-white rounded-lg shadow">
					<p className="text-gray-500">No matches found matching your search criteria.</p>
				</div>
			)}
		</div>
	);
};

export default Matches;
